import { TypeGuards, useI18N, useMemo } from '@codeleap/common'
import { modal, ModalParams } from '@codeleap/modals'
import * as ShareOptions from 'react-share'

import { IS_SSR, React, Settings, variantProvider } from '@/app'
import { ActivityIndicator, Button, Text, Tooltip, View } from '@/components'
import { APIClient } from '@/services'
import { createLog, ShareData } from '@/utils'

const shareOptions = [
  [ShareOptions.EmailShareButton, ShareOptions.EmailIcon],
  [ShareOptions.WhatsappShareButton, ShareOptions.WhatsappIcon],
  [ShareOptions.FacebookShareButton, ShareOptions.FacebookIcon],
  [ShareOptions.LinkedinShareButton, ShareOptions.LinkedinIcon, 'linkedIn'],
  [ShareOptions.TelegramShareButton, ShareOptions.TelegramIcon],
  [ShareOptions.TumblrShareButton, ShareOptions.TumblrIcon],
  [ShareOptions.InstapaperShareButton, ShareOptions.InstapaperIcon, 'instapaper']
]

function extractUrl(msg: string): string {
  const match = msg?.match(/https?:\/\/[^\s]+/)
  return match ? match[0] : ''
}

const MAX_WIDTH = 500
const ICON_SIZE = 50
const DEBUG = false
const log = createLog('ShareModal', DEBUG)

const ShareModalContent = (props: ModalParams<ShareData, boolean>) => {
  const { request, ...shareData } = props

  const { t } = useI18N()

  const visible = React.useMemo(() => !TypeGuards?.isNil(shareData), [shareData])

  const urlOrData = shareData?.type === 'link' ? shareData?.url : shareData?.data

  const content = {
    [shareData?.type === 'link' ? 'url' : 'data']: urlOrData,
    title: shareData?.text,
    description: shareData?.text,
    type: shareData?.type,
    text: shareData?.text,
    summary: shareData?.text,
    quote: shareData?.title,
    appId: Settings.ApiCredentials.FacebookSDK.AppId,
    separator: ' '
  }

  const canClipboard = useMemo(() => {
    if (IS_SSR) return

    return !!navigator.clipboard
  }, [])

  const getContent = React.useCallback(() => [content.text, urlOrData].filter(Boolean).join('\n'), [content, urlOrData])

  const copyLink = React.useCallback(async () => {
    const content = getContent()
    if (!content) return

    await navigator.clipboard.writeText(content)
    await APIClient.Session.sharedApp()

    // request.resolve(true)
  }, [getContent])

  const sharedApp = async () => {
    log('Shared app initiated', { content, urlOrData })
    request.resolve(true)
    await APIClient.Session.sharedApp()
  }

  const options = React.useMemo(() => {
    return shareOptions.map(([ShareAction, Icon, provider = null]) => {
      let url = getContent()

      if (provider === 'linkedIn' || provider === 'instapaper') {
        url = extractUrl(url) || url
      }

      const isEmail = ShareAction === ShareOptions.EmailShareButton

      const handleClick = isEmail
        ? (_e, link) => {
            window.location.href = link
            sharedApp()
          }
        : sharedApp

      log('Sharing content', { url, provider })

      return (
        <ShareAction url={url} style={styles.share} onClick={handleClick}>
          <Icon size={ICON_SIZE} url={url}>
            {null}
          </Icon>
        </ShareAction>
      )
    })
  }, [sharedApp, getContent])

  return (
    <>
      <View variants={['column', 'fullWidth', 'alignStart', 'justifyCenter', 'marginBottom:1']}>
        <Text variants={['h1', 'ellipsis']} text={shareData?.title ?? t('share.title')} style={styles.title} />
      </View>

      <View variants={['column', 'fullWidth', 'alignStart', 'justifyCenter', 'gap:3']}>
        <Text variants={['p1']} style={styles.text} text={shareData?.text ?? t('share.description')} />

        <View variants={['row', 'wrap', 'gap:2', 'center']}>{options}</View>

        <View variants={['fullWidth', 'row', 'gap:2']}>
          {canClipboard ? (
            <Tooltip
              debugName={'shareModal:tooltip'}
              content={<Text text={t('share.copyMessage')} variants={['p2', 'color:positive2']} />}
              openOnPress
              openOnHover={false}
              side="top"
              closeOnClickOutside
              variants={['small', 'success']}
              // @ts-ignore
              style={styles.tooltip}
            >
              <Button
                debugName="shareModal:clipboard"
                text={content?.type === 'link' ? t('share.copyLink') : t('share.copyText')}
                onPress={copyLink}
                variants={['outline', 'fullWidth', 'large', 'pill', 'flex']}
              />
            </Tooltip>
          ) : null}

          <Button
            debugName="shareModal:done"
            text={t('share.modalCancel')}
            onPress={() => request.resolve(false)}
            variants={['large', 'pill', 'flex', 'fullWidth']}
          />
        </View>
      </View>

      {visible ? null : (
        <View variants={['absolute', 'whole', 'center', 'backgroundColor:neutral1']}>
          <ActivityIndicator debugName="shareModal:indicator" />
        </View>
      )}
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    boxModal: {
      width: `calc(100vw - ${theme.spacing.value(4)}px)`,
      maxWidth: MAX_WIDTH,
      ...theme.spacing.padding(4),
      borderRadius: theme.borderRadius.large,

      [theme.media.down('small')]: {
        ...theme.spacing.padding(2)
      }
    },
    share: {
      borderRadius: theme.borderRadius.tiny,
      overflow: 'hidden',
      width: ICON_SIZE,
      height: ICON_SIZE
    },
    title: {
      ...theme.presets.fullWidth
    },
    text: {
      ...theme.presets.fullWidth,
      whiteSpace: 'pre-wrap',
      ...theme.presets.textCenter
    },
    tooltip: {
      width: '100%',
      flex: 1,
      ...theme.presets.textCenter
    }
  }),
  true
)

export const ShareModal = modal<ShareData, boolean>()
  .content(ShareModalContent)
  .props({
    showClose: false,
    variants: ['centered'],
    styles: {
      wrapper: { zIndex: 99999 },
      box: styles.boxModal
    }
  })
